import type React from 'react';
import { createContext, useContext, useMemo } from 'react';

import { breakpoints } from '@coursera/cds-common';

import { useMediaQuery } from '@core/utils';

export type CardSize = 'standard' | 'compact';

export type ProductCardExperimentVariant =
  | 'control'
  | 'test1'
  | 'test1.1'
  | 'test1.2'
  | 'test1.3'
  | 'test1.4';

export type CardContextValue = {
  size?: CardSize;
  isXs?: boolean;
  variant?: 'grid' | 'list';
  isCompactStyle?: boolean;
  /** True when compact layout OR any test1 family variant; drives bodyTertiary, subtitleSmall, etc. across all card sub-components. Does not affect root .compact class. */
  useCompactTypography?: boolean;
  /** True when compact layout OR test1 (not test1.1/test1.2); drives partner avatar size and partner name typography. */
  useCompactPartnerInfo?: boolean;
  /** ProductCard experiment variant; used for test1 spacing/typography. */
  experimentVariant?: ProductCardExperimentVariant;
  /** Whether the card shows a preview image (affects test1 typography in no-image layout). */
  hasPreviewImage?: boolean;
};

/**
 * Utility function to determine if compact styling should be applied.
 */
export const getIsCompactStyle = (
  variant?: 'grid' | 'list',
  size?: CardSize,
  isXs?: boolean
): boolean => {
  return variant === 'list'
    ? size === 'compact'
    : Boolean(isXs && size === 'compact');
};

export const CardContext = createContext<CardContextValue | undefined>(
  undefined
);

export const useCardContext = () => {
  return useContext(CardContext);
};

type CardContextProviderProps = {
  size?: CardSize;
  variant?: 'grid' | 'list';
  experimentVariant?: ProductCardExperimentVariant;
  children: React.ReactNode;
};

export const CardContextProvider = ({
  size,
  variant,
  experimentVariant,
  children,
}: CardContextProviderProps) => {
  const isXs = useMediaQuery(breakpoints.down('xs'));

  const isCompactStyle = getIsCompactStyle(variant, size, isXs);
  const value = useMemo(() => {
    const isTest1CompactTypography =
      experimentVariant === 'test1' ||
      experimentVariant === 'test1.1' ||
      experimentVariant === 'test1.2' ||
      experimentVariant === 'test1.3';
    return {
      size,
      isXs,
      variant,
      isCompactStyle,
      useCompactTypography: isCompactStyle || isTest1CompactTypography,
      useCompactPartnerInfo: isCompactStyle || experimentVariant === 'test1',
      experimentVariant,
    };
  }, [size, isXs, variant, isCompactStyle, experimentVariant]);

  return <CardContext.Provider value={value}>{children}</CardContext.Provider>;
};
